// Randevu ve sipariş bildirimleri: e-posta (EmailJS) + Google Sheets (Apps Script).
//
// Bu servis iki yeri birden besler:
//   - Site sahibine e-posta
//   - Google Drive'daki tabloya satır ekleme
//
// ÖNEMLİ - eski koddaki sessiz hata: Sheets isteği `mode: 'no-cors'` ile
// gönderiliyordu. O modda cevap okunamaz, bu yüzden istek başarısız olsa bile
// kod "kaydedildi" sayıyordu. Nitekim eski Apps Script adresi 403 dönüyordu ve
// aylardır hiçbir randevu tabloya yazılmıyordu, kimse fark etmemişti.
//
// Çözüm: istek `text/plain` olarak gönderiliyor. Bu bir "simple request"
// sayıldığı için tarayıcı preflight yapmaz, Apps Script de cevabı okumamıza
// izin verir. Böylece gerçekten yazıldı mı görebiliyoruz.

export const NOTIFICATION_CONFIG = {
  // Randevu ve siparişlerin bildirileceği adres
  ownerEmail: 'sultanasan16@gmail.com',

  email: {
    enabled: true,
    serviceId: 'service_n81t6gp',
    templateId: 'template_bsurgqo',
    publicKey: 'h6L8vjsLujpkX_Y4U',
  },

  sheets: {
    enabled: true,
    // GOOGLE_APPS_SCRIPT_KURULUM.md dosyasindaki adimlarla olusturulan
    // Apps Script deployment adresi buraya yazilir.
    scriptUrl: 'https://script.google.com/macros/s/AKfycbwKtDuU0Zaiiz-1vyeE-qHZkh2KKDMcTPrDUTsqYJuy8M_JFyBkF_w8O4boQvTTpWJ1/exec',
  },
};

export type NotificationType = 'appointment' | 'order';

export interface SheetResult {
  ok: boolean;
  error?: string;
}

// --- Google Sheets ---
export async function appendToSheet(
  type: NotificationType,
  row: Record<string, any>
): Promise<SheetResult> {
  const { enabled, scriptUrl } = NOTIFICATION_CONFIG.sheets;

  if (!enabled) {
    return { ok: false, error: 'Sheets entegrasyonu kapali' };
  }
  if (!scriptUrl) {
    console.warn('[bildirim] Apps Script adresi tanimli degil, tabloya yazilmadi.');
    return { ok: false, error: 'Apps Script adresi tanimli degil' };
  }

  try {
    const response = await fetch(scriptUrl, {
      method: 'POST',
      // text/plain => preflight yok, cevap okunabilir
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        type,
        timestamp: new Date().toISOString(),
        ...row,
      }),
    });

    if (!response.ok) {
      const detail = `HTTP ${response.status}`;
      console.error('[bildirim] Tabloya yazilamadi:', detail);
      return { ok: false, error: detail };
    }

    // Apps Script { ok: true } dondurur
    const text = await response.text();
    let parsed: any = null;
    try {
      parsed = JSON.parse(text);
    } catch {
      // Script duz metin donduyse de basarili sayalim
      return { ok: true };
    }

    if (parsed && parsed.ok === false) {
      console.error('[bildirim] Apps Script hata bildirdi:', parsed.error);
      return { ok: false, error: parsed.error || 'Apps Script hatasi' };
    }

    return { ok: true };
  } catch (error: any) {
    console.error('[bildirim] Tabloya yazma istegi basarisiz:', error);
    return { ok: false, error: error?.message || 'Baglanti hatasi' };
  }
}

// --- E-posta (EmailJS) ---
export async function sendOwnerEmail(templateParams: Record<string, any>): Promise<boolean> {
  const { enabled, serviceId, templateId, publicKey } = NOTIFICATION_CONFIG.email;

  if (!enabled) return false;

  try {
    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        template_params: {
          to_email: NOTIFICATION_CONFIG.ownerEmail,
          ...templateParams,
        },
      }),
    });

    if (!response.ok) {
      console.error('[bildirim] E-posta gonderilemedi:', response.status, await response.text());
      return false;
    }
    return true;
  } catch (error) {
    console.error('[bildirim] E-posta istegi basarisiz:', error);
    return false;
  }
}

// --- Sipariş bildirimi ---
// Siparişin kendisi Firestore'a yazıldıktan SONRA çağrılır ve asla hata
// fırlatmaz; bildirim gitmese bile sipariş geçerli kalmalı.
export async function notifyNewOrder(order: {
  orderNumber: string;
  totalAmount: number;
  paymentId: string;
  customer: {
    name: string;
    surname: string;
    email: string;
    phone: string;
    address: string;
    city: string;
  };
  items: Array<{ name: string; quantity: number; price: number; size?: string }>;
}): Promise<void> {
  try {
    const itemLines = order.items
      .map((i) => `- ${i.name}${i.size ? ` (Beden: ${i.size})` : ''} x${i.quantity} — ${i.price.toLocaleString('tr-TR')} TL`)
      .join('\n');

    const customerName = `${order.customer.name} ${order.customer.surname}`.trim();

    const summary = [
      `Sipariş No: ${order.orderNumber}`,
      `Tutar: ${order.totalAmount.toLocaleString('tr-TR')} TL`,
      `Ödeme ID: ${order.paymentId}`,
      '',
      `Müşteri: ${customerName}`,
      `Telefon: ${order.customer.phone}`,
      `E-posta: ${order.customer.email}`,
      `Adres: ${order.customer.address}, ${order.customer.city}`,
      '',
      'Ürünler:',
      itemLines,
    ].join('\n');

    await Promise.all([
      // 1) Musteriye siparis onayi - standart e-ticaret davranisi.
      //    Eskiden yalnizca site sahibine mail gidiyordu.
      sendCustomerEmail(order.customer.email, {
        from_name: 'Saraylı Moda',
        from_email: NOTIFICATION_CONFIG.ownerEmail,
        phone: '-',
        service_type: 'Sipariş Onayı',
        appointment_date: '-',
        appointment_time: '-',
        message: [
          `Sayın ${customerName},`,
          '',
          'Siparişiniz başarıyla alındı. Teşekkür ederiz.',
          '',
          summary,
          '',
          'Siparişinizin durumunu sitemizdeki "Sipariş Takip" sayfasından',
          `${order.orderNumber} numarası ile takip edebilirsiniz.`,
          '',
          'Saraylı Moda',
        ].join('\n'),
      }),

      // 2) Site sahibine bildirim
      sendOwnerEmail({
        from_name: customerName,
        from_email: order.customer.email,
        phone: order.customer.phone,
        service_type: 'Yeni Sipariş',
        appointment_date: '-',
        appointment_time: '-',
        message: summary,
      }),
      appendToSheet('order', {
        orderNumber: order.orderNumber,
        customerName,
        email: order.customer.email,
        phone: order.customer.phone,
        address: `${order.customer.address}, ${order.customer.city}`,
        totalAmount: order.totalAmount,
        paymentId: order.paymentId,
        items: order.items
          .map((i) => `${i.name}${i.size ? ` (${i.size})` : ''} x${i.quantity}`)
          .join(' | '),
      }),
    ]);
  } catch (error) {
    // Bildirim hatasi siparisi etkilemez
    console.error('[bildirim] Siparis bildirimi gonderilemedi:', error);
  }
}

// --- Musteriye e-posta ---
// sendOwnerEmail her zaman site sahibine gonderir; bu ise alicisi musteridir.
async function sendCustomerEmail(
  toEmail: string,
  templateParams: Record<string, any>
): Promise<boolean> {
  const { enabled, serviceId, templateId, publicKey } = NOTIFICATION_CONFIG.email;
  if (!enabled || !toEmail) return false;

  try {
    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        template_params: { to_email: toEmail, ...templateParams },
      }),
    });

    if (!response.ok) {
      console.error('[bildirim] Musteri e-postasi gonderilemedi:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    console.error('[bildirim] Musteri e-posta istegi basarisiz:', error);
    return false;
  }
}

// Siparis durumu degistiginde musteriye gonderilen aciklamalar
const ORDER_STATUS_MESSAGES: Record<string, string> = {
  'Alındı': 'Siparişiniz tarafımıza ulaştı. En kısa sürede hazırlanmaya başlanacak.',
  'Hazırlanıyor': 'Siparişiniz şu anda hazırlanıyor.',
  'Kargolandı': 'Siparişiniz kargoya verildi. Kısa süre içinde elinizde olacak.',
  'Teslim Edildi': 'Siparişiniz teslim edildi. Bizi tercih ettiğiniz için teşekkür ederiz.',
  'İptal Edildi': 'Siparişiniz iptal edilmiştir.',
};

// Durum degisikligini siparis sahibine bildirir.
// Hata firlatmaz; bildirim gitmese bile durum guncellemesi gecerli kalir.
export async function notifyOrderStatusChange(params: {
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  newStatus: string;
  note?: string;
}): Promise<boolean> {
  const { orderNumber, customerName, customerEmail, newStatus, note } = params;

  try {
    const lines = [
      `Sayın ${customerName},`,
      '',
      `${orderNumber} numaralı siparişinizin durumu güncellendi.`,
      '',
      `Yeni durum: ${newStatus}`,
      ORDER_STATUS_MESSAGES[newStatus] || '',
    ];

    if (note && note.trim()) {
      lines.push('', `Açıklama: ${note.trim()}`);
    }

    lines.push('', 'Saraylı Moda');

    return await sendCustomerEmail(customerEmail, {
      from_name: 'Saraylı Moda',
      from_email: NOTIFICATION_CONFIG.ownerEmail,
      phone: '-',
      service_type: `Sipariş Durumu: ${newStatus}`,
      appointment_date: '-',
      appointment_time: '-',
      message: lines.filter((l, i, arr) => !(l === '' && arr[i - 1] === '')).join('\n'),
    });
  } catch (error) {
    console.error('[bildirim] Durum bildirimi gonderilemedi:', error);
    return false;
  }
}
